import { create } from "zustand";
import { isMarketingSitePath } from "../pages/marketing-site/marketingSiteRoutes";
import { changeColorThemeWithCrossfade } from "./colorThemeCrossfade";
import { DayPhase, getCurrentDayPhase } from "./dayNightSchedule";
import {
  getColorThemeScopeForPath,
  isTwilightSceneShowing,
  JungleColorTheme,
  JungleColorThemePreference,
  readStoredAdminToolsColorMode,
  readStoredColorThemePreference,
  resolveColorTheme,
  storeAdminToolsColorMode,
  storeColorThemePreference,
} from "./jungleColorTheme";

type JungleColorThemeState = {
  colorThemePreference: JungleColorThemePreference;
  // Day, twilight or night where the person is; only matters while the preference is "auto".
  // JungleColorThemeSync keeps it current.
  dayPhase: DayPhase;
  // True while the page is in Jungle's dark theme (false on the light theme, an admin page, a marketing
  // page). JungleColorThemeSync keeps it current; artwork that has its own night version reads it to pick
  // the file.
  isDarkThemeActive: boolean;
  // True in the hour in which the light page shows its scenes at dusk (isTwilightSceneShowing)
  isTwilightSceneActive: boolean;
  setColorThemePreference: (colorThemePreference: JungleColorThemePreference) => void;
  setDayPhase: (dayPhase: DayPhase) => void;
  setArtworkState: (artworkState: { isDarkThemeActive: boolean; isTwilightSceneActive: boolean }) => void;
};

type ColorThemeInputs = Pick<JungleColorThemeState, "colorThemePreference" | "dayPhase">;

// Whether new inputs change what the current page shows: its theme, or its scenes turning to dusk and
// back. Only an app page follows the preference, the device and the time of day; a marketing page stays
// light and the admin area has its own switch, so nothing there fades.
function wouldChangeTheLookOfThisPage(currentInputs: ColorThemeInputs, nextInputs: ColorThemeInputs): boolean {
  const pathname = window.location.pathname;
  const colorThemeScope = getColorThemeScopeForPath(pathname, isMarketingSitePath(pathname));
  if (colorThemeScope !== "app") return false;
  const describeLookOfThisPage = (inputs: ColorThemeInputs) =>
    resolveColorTheme({ colorThemeScope, ...inputs, adminToolsColorMode: "light" }) +
    (isTwilightSceneShowing({ colorThemeScope, ...inputs }) ? " at twilight" : "");
  return describeLookOfThisPage(currentInputs) !== describeLookOfThisPage(nextInputs);
}

export const useJungleColorThemeStore = create<JungleColorThemeState>((set, get) => {
  // The browser applies a faded change a frame later. Until then the change waits here, and any newer
  // change joins it instead of landing first: otherwise the older change would land last and win (two
  // quick taps in Settings by day: "dark", then "auto").
  let inputsWaitingForCrossfade: Partial<ColorThemeInputs> | null = null;

  // A change that turns the page dark or light, or its scenes to dusk, fades (colorThemeCrossfade.ts);
  // any other change is stored at once, so choosing "dark" at night, when automatic already shows dark,
  // does not flash a fade.
  const updateColorThemeInputs = (nextInputs: Partial<ColorThemeInputs>) => {
    if (inputsWaitingForCrossfade) {
      inputsWaitingForCrossfade = { ...inputsWaitingForCrossfade, ...nextInputs };
      return;
    }
    const currentInputs: ColorThemeInputs = {
      colorThemePreference: get().colorThemePreference,
      dayPhase: get().dayPhase,
    };
    if (!wouldChangeTheLookOfThisPage(currentInputs, { ...currentInputs, ...nextInputs })) {
      set(nextInputs);
      return;
    }
    inputsWaitingForCrossfade = nextInputs;
    changeColorThemeWithCrossfade(() => {
      const inputsToApply = inputsWaitingForCrossfade;
      inputsWaitingForCrossfade = null;
      if (inputsToApply) set(inputsToApply);
    });
  };

  return {
    colorThemePreference: readStoredColorThemePreference(),
    dayPhase: getCurrentDayPhase(),
    isDarkThemeActive: false,
    isTwilightSceneActive: false,
    setColorThemePreference: (colorThemePreference) => {
      storeColorThemePreference(colorThemePreference);
      updateColorThemeInputs({ colorThemePreference });
    },
    // Called every minute: only a real change goes further
    setDayPhase: (dayPhase) => {
      if (dayPhase !== get().dayPhase) updateColorThemeInputs({ dayPhase });
    },
    setArtworkState: (artworkState) => set(artworkState),
  };
});

export function resolveColorThemeForPath(pathname: string): JungleColorTheme {
  const { colorThemePreference, dayPhase } = useJungleColorThemeStore.getState();
  return resolveColorTheme({
    colorThemeScope: getColorThemeScopeForPath(pathname, isMarketingSitePath(pathname)),
    colorThemePreference,
    dayPhase,
    adminToolsColorMode: readStoredAdminToolsColorMode(),
  });
}

// Chakra asks its color mode manager once, when its provider mounts, and tells it about every change.
// On an app page Jungle's own preference is the only stored choice, so nothing is stored here. In the
// admin area a change can only be its own light/dark switch, which keeps the storage it always had.
export const jungleChakraColorModeManager = {
  type: "localStorage" as const,
  ssr: false,
  get: (): JungleColorTheme => resolveColorThemeForPath(window.location.pathname),
  set: (colorMode: JungleColorTheme | "system"): void => {
    const pathname = window.location.pathname;
    if (getColorThemeScopeForPath(pathname, isMarketingSitePath(pathname)) !== "adminTools") return;
    storeAdminToolsColorMode(colorMode === "dark" ? "dark" : "light");
  },
};
